(() => {
  'use strict';
  const root = document.getElementById('af-ebay-generator');
  if (!root) return;
  const get = selector => root.querySelector(selector);
  const status = get('[data-status]');
  const result = get('[data-result]');
  const output = get('[data-output]');
  const preview = get('[data-preview]');
  const editor = get('[data-description]');
  const tokens = /\[ITEM TITLE\]|\[ITEM DESCRIPTION\]/g;
  const allowed = new Set(['DIV','P','SPAN','H1','H2','H3','H4','H5','H6','STRONG','B','EM','I','U','UL','OL','LI','BR','HR','BLOCKQUOTE','TABLE','THEAD','TBODY','TFOOT','TR','TH','TD','IMG']);
  const remove = new Set(['SCRIPT','STYLE','LINK','META','IFRAME','OBJECT','EMBED','FORM','INPUT','BUTTON','TEXTAREA','SELECT','SVG','MATH','TEMPLATE','BASE','NOSCRIPT']);
  const safeStyles = new Set(['color','background-color','font-family','font-size','font-weight','font-style','text-align','text-decoration','line-height','letter-spacing','margin','margin-top','margin-bottom','margin-left','margin-right','padding','padding-top','padding-bottom','padding-left','padding-right','border','border-top','border-bottom','border-left','border-right','border-color','border-width','border-style','border-radius','border-collapse','width','max-width','height','vertical-align']);
  const defaults = {
    P:'margin:0 0 16px;', H1:'font-size:28px;line-height:1.25;margin:24px 0 14px;',
    H2:'font-size:24px;line-height:1.3;margin:24px 0 14px;',
    H3:'font-size:20px;line-height:1.35;margin:24px 0 12px;',
    H4:'font-size:17px;margin:20px 0 10px;',
    UL:'margin:0 0 16px;padding-left:24px;line-height:1.35;', OL:'margin:0 0 16px;padding-left:24px;line-height:1.35;',
    LI:'margin:0;padding:0;line-height:1.35;', HR:'border:0;border-top:1px solid #ddd;margin:24px 0;',
    TABLE:'width:100%;border-collapse:collapse;', IMG:'max-width:100%;height:auto;'
  };

  // Product descriptions accept a small formatting vocabulary, never pasted scripts or styles.
  function cleanDescription(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const target = document.createElement('div');
    const richTags = new Set(['P','DIV','BR','UL','OL','LI','STRONG','B','EM','I','U']);
    function copy(node, parent) {
      if (node.nodeType === Node.TEXT_NODE) {
        parent.appendChild(document.createTextNode(node.nodeValue.replace(/\u00a0|&nbsp;|&#160;|&#x0*a0;/gi, ' ')));
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE || remove.has(node.tagName)) return;
      let dest = parent;
      if (richTags.has(node.tagName)) {
        dest = document.createElement(node.tagName === 'DIV' ? 'p' : node.tagName.toLowerCase());
        if (defaults[dest.tagName]) dest.setAttribute('style',defaults[dest.tagName]);
        parent.appendChild(dest);
      } else if (node.tagName === 'SPAN' && (/^(bold|[6-9]00)$/.test(node.style.fontWeight) || node.style.fontStyle === 'italic')) {
        dest = document.createElement(/^(bold|[6-9]00)$/.test(node.style.fontWeight) ? 'strong' : 'em');
        parent.appendChild(dest);
      }
      Array.from(node.childNodes).forEach(child => copy(child,dest));
    }
    Array.from(parsed.body.childNodes).forEach(node => copy(node,target));
    // Reparse to normalize paragraph boundaries around nested block content.
    const normalized = new DOMParser().parseFromString(target.innerHTML,'text/html');
    normalized.body.querySelectorAll('p').forEach(p => {if (!p.textContent.trim() && !p.querySelector('br')) p.remove();});
    // Keep Shopify's soft breaks as BR elements and avoid adding paragraph gaps inside lists.
    normalized.body.querySelectorAll('ul,ol').forEach(list => {
      list.style.lineHeight = '1.35';
      list.style.listStyleType = list.tagName === 'UL' ? 'disc' : 'decimal';
    });
    normalized.body.querySelectorAll('li').forEach(li => {
      li.style.margin = '0';
      li.style.padding = '0';
      li.style.lineHeight = '1.35';
    });
    normalized.body.querySelectorAll('li p').forEach(p => {p.style.margin = '0';});
    return normalized.body.innerHTML;
  }
  function plainDescription(text) {
    const holder = document.createElement('div');
    text.replace(/\r\n?/g,'\n').split(/\n\s*\n/).forEach(paragraph => {
      const p = document.createElement('p');
      paragraph.split('\n').forEach((line,index) => {
        if (index) p.appendChild(document.createElement('br'));
        p.appendChild(document.createTextNode(line));
      });
      holder.appendChild(p);
    });
    return cleanDescription(holder.innerHTML);
  }
  editor.addEventListener('paste',event => {
    if (!event.clipboardData) return;
    event.preventDefault();
    const rich = event.clipboardData.getData('text/html');
    const plain = event.clipboardData.getData('text/plain');
    const html = rich ? cleanDescription(rich) : plainDescription(plain);
    const selection = window.getSelection();
    if (!selection.rangeCount || !editor.contains(selection.getRangeAt(0).commonAncestorContainer)) return;
    const range = selection.getRangeAt(0);
    range.deleteContents();
    const fragment = range.createContextualFragment(html);
    const last = fragment.lastChild;
    range.insertNode(fragment);
    if (last) {range.setStartAfter(last);range.collapse(true);selection.removeAllRanges();selection.addRange(range);}
    clearResult();
  });
  // Sanitize dragged content too, without accepting files or remote images.
  editor.addEventListener('drop',event => {event.preventDefault();status.textContent = 'Please paste the description rather than dragging it.';});

  function build(source, title, description) {
    const doc = new DOMParser().parseFromString(source, 'text/html');
    let cleaned = false;
    // Keep static formatting only. Links become text; image URLs must use HTTPS.
    for (const el of Array.from(doc.body.querySelectorAll('*'))) {
      if (!doc.body.contains(el)) continue;
      if (remove.has(el.tagName)) {el.remove(); cleaned = true; continue;}
      if (!allowed.has(el.tagName)) {el.replaceWith(...el.childNodes); cleaned = true; continue;}
      const styles = [];
      for (const prop of Array.from(el.style)) {
        const value = el.style.getPropertyValue(prop);
        if (safeStyles.has(prop) && !/url\s*\(|expression|@|\\/i.test(value)) styles.push(prop + ':' + value + ';');
        else cleaned = true;
      }
      let imageURL = null;
      const alt = el.getAttribute('alt') || '';
      if (el.tagName === 'IMG') {
        try {
          const url = new URL(el.getAttribute('src') || '', 'https://aquariumfish.com');
          if (url.protocol === 'https:' && !url.username && !url.password) imageURL = url.href;
        } catch (_) {}
        if (!imageURL) {el.remove(); cleaned = true; continue;}
      }
      const spanAttrs = {};
      if (el.tagName === 'TD' || el.tagName === 'TH') {
        for (const name of ['colspan','rowspan']) {
          const value = el.getAttribute(name);
          if (/^[1-9]\d{0,2}$/.test(value || '')) spanAttrs[name] = value;
        }
      }
      for (const attr of Array.from(el.attributes)) {
        if (/^on/i.test(attr.name)) cleaned = true;
        el.removeAttribute(attr.name);
      }
      if (defaults[el.tagName] || styles.length) el.setAttribute('style', (defaults[el.tagName] || '') + styles.join(''));
      for (const [name,value] of Object.entries(spanAttrs)) el.setAttribute(name,value);
      if (imageURL) {el.setAttribute('src',imageURL);el.setAttribute('alt',alt);}
    }
    // Use the same tight list spacing for the foundational source content.
    doc.body.querySelectorAll('ul,ol').forEach(list => {list.style.lineHeight = '1.35';});
    doc.body.querySelectorAll('li').forEach(li => {
      li.style.margin = '0';
      li.style.padding = '0';
      li.style.lineHeight = '1.35';
    });
    doc.body.querySelectorAll('li p').forEach(p => {p.style.margin = '0';});
    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    let titles = 0, descriptions = 0;
    for (const node of nodes) {
      if (!doc.body.contains(node)) continue;
      const text = node.nodeValue;
      const matches = Array.from(text.matchAll(tokens));
      if (!matches.length) continue;
      if (text.includes('[ITEM DESCRIPTION]')) {
        const block = node.parentElement.closest('p,div');
        if (!block || block === doc.body || block.textContent.trim() !== '[ITEM DESCRIPTION]') {
          throw new Error('Put [ITEM DESCRIPTION] by itself in a paragraph or div on the source page.');
        }
        const holder = doc.createElement('div');
        holder.innerHTML = cleanDescription(description);
        block.replaceWith(...holder.childNodes);
        descriptions++;
        continue;
      }
      const fragment = doc.createDocumentFragment();
      let position = 0;
      for (const match of matches) {
        fragment.appendChild(doc.createTextNode(text.slice(position, match.index)));
        const isTitle = match[0] === '[ITEM TITLE]';
        if (isTitle) titles++; else descriptions++;
        const lines = (isTitle ? title : description).replace(/\r\n?/g,'\n').split('\n');
        lines.forEach((line,index) => {
          if (index) fragment.appendChild(doc.createElement('br'));
          fragment.appendChild(doc.createTextNode(line));
        });
        position = match.index + match[0].length;
      }
      fragment.appendChild(doc.createTextNode(text.slice(position)));
      node.replaceWith(fragment);
    }
    if (!titles || !descriptions) throw new Error('The source page must contain [ITEM TITLE] and [ITEM DESCRIPTION]. Type each placeholder as one continuous piece of text without mixed formatting.');
    const wrapper = doc.createElement('div');
    wrapper.setAttribute('style','box-sizing:border-box;width:100%;max-width:1000px;margin:0 auto;padding:20px;font-family:Arial,Helvetica,sans-serif;font-size:16px;line-height:1.6;color:#222;background-color:#fff;overflow-wrap:break-word;');
    wrapper.append(...doc.body.childNodes);
    return {html:wrapper.outerHTML, cleaned};
  }
  function clearResult() {
    result.hidden = true;
    output.value = '';
    preview.removeAttribute('srcdoc');
    status.textContent = '';
  }
  get('[data-form]').addEventListener('input',clearResult);
  get('[data-form]').addEventListener('submit',event => {
    event.preventDefault();
    clearResult();
    try {
      const title = get('[data-title]').value.trim();
      const description = cleanDescription(editor.innerHTML);
      const check = new DOMParser().parseFromString(description,'text/html');
      if (!title || !check.body.textContent.trim()) throw new Error('Enter both an item title and description.');
      const source = get('[data-source]').value;
      if (!source.trim()) throw new Error('Source page unavailable. Check that ebay-page-formatter is published to the Online Store.');
      const generated = build(source,title,description);
      output.value = generated.html;
      preview.srcdoc = '<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head><body style="margin:0">' + generated.html + '</body></html>';
      result.hidden = false;
      status.textContent = generated.cleaned ? 'HTML ready. Links or unsupported source content were removed. Check the preview before copying.' : 'HTML ready. Check the preview, then copy.';
    } catch (error) {status.textContent = error.message;}
  });
  get('[data-copy]').addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(output.value);
      status.textContent = 'Copied! Paste into the HTML tab of the eBay description editor.';
    } catch (_) {
      output.closest('details').open = true;
      output.focus(); output.select();
      status.textContent = 'Code selected. Press Ctrl+C (Windows) or Command+C (Mac) to copy.';
    }
  });
})();
